import { Link } from '@tanstack/react-router';
import { CornerDownRight, ListChecks, Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { api, call, type Schemas } from '../../api/client';
import { useAction, useTasks } from '../../api/queries';
import { useHousehold } from '../../api/session';
import { Button, Card, Input } from '../../components/ui';
import { TaskCard } from './TaskCard';

type Task = Schemas['TaskResponse'];

/** "2 of 5 done" with a bar. */
export function SubtaskProgress({ progress, compact = false }: { progress: Task['subtasks']; compact?: boolean }) {
  if (!progress.total) return null;
  const pct = Math.round((progress.completed / progress.total) * 100);
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-[var(--muted)]" title={`${progress.completed} of ${progress.total} subtasks done`}>
      <ListChecks size={12} />
      <span className="h-1.5 w-12 overflow-hidden rounded-full bg-[var(--surface-2)]" aria-hidden>
        <span className="block h-full rounded-full bg-[var(--accent)]" style={{ width: `${pct}%` }} />
      </span>
      {compact ? `${progress.completed}/${progress.total}` : `${progress.completed} of ${progress.total} done`}
    </span>
  );
}

/** "↳ Part of “Finish the deck”" on a subtask. */
export function ParentLink({ parent }: { parent: Task['parent'] }) {
  if (!parent) return null;
  return (
    <Link to="/tasks/$taskId" params={{ taskId: parent.id }} className="inline-flex items-center gap-1 text-xs text-[var(--muted)] hover:underline">
      <CornerDownRight size={12} /> Part of “{parent.title}”
    </Link>
  );
}

/** Subtasks of a task (ADR-0019): each is a task of its own that anyone can take; the parent completes when all are done. */
export function Subtasks({ task }: { task: Task }) {
  const { familyId } = useHousehold();
  const canAdd = task.allowedActions.includes('ADD_SUBTASK');
  const subtasks = useTasks({ parentTaskId: task.id, status: 'all', sort: 'created' }, task.subtasks.total > 0 || canAdd).data?.items.filter((t) => t.status !== 'ARCHIVED');
  const [title, setTitle] = useState('');
  const [mine, setMine] = useState(false);
  const add = useAction(
    (input: { title: string; claim: boolean }) =>
      call(api.POST('/api/v1/families/{familyId}/tasks', { params: { path: { familyId } }, body: { ...input, parentTaskId: task.id } })),
    { success: 'Subtask added' },
  );
  if (!canAdd && !task.subtasks.total) return null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    const submitted = title.trim();
    // Runs after the list refreshed: clear only if the box still holds what was added, not the next one being typed.
    add.mutate({ title: submitted, claim: mine }, { onSuccess: () => setTitle((current) => (current.trim() === submitted ? '' : current)) });
  };

  return (
    <section className="mb-6">
      <h2 className="mb-2 flex items-center gap-3 text-sm font-medium text-[var(--muted)]">
        Subtasks <SubtaskProgress progress={task.subtasks} />
      </h2>
      {task.subtasks.open > 0 ? <p className="mb-2 text-xs text-[var(--muted)]">This task completes on its own once every subtask is done.</p> : null}
      <ul className="space-y-2">
        {(subtasks ?? []).map((s) => (
          <li key={s.id}>
            <TaskCard task={s} hideParent />
          </li>
        ))}
      </ul>
      {canAdd ? (
        <Card className="mt-2">
          <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
            <div className="min-w-48 flex-1">
              <Input aria-label="New subtask" placeholder="Add a subtask, e.g. Finish the stairs" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <label className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> I'll do it
            </label>
            <Button type="submit" size="sm" busy={add.isPending} disabled={!title.trim()}>
              <Plus size={15} /> Add
            </Button>
          </form>
        </Card>
      ) : null}
    </section>
  );
}
